import React, { useEffect, useState } from "react";
import { Zap, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { startPulse, stopPulse } from "@lawyer/services/lawyerApi";

const DURATIONS = [5, 15, 30];

/**
 * Lawyer-side "Available Right Now" pulse control.
 * Shown only when lawyer is verified + currently online.
 *
 * Renders one of two states:
 *   - Inactive: Zap CTA + duration chip selector + optional message field
 *   - Active:   live emerald banner with countdown + Stop button
 */
export default function LawyerPulseCard({ lawyer, onChanged }) {
  const [duration, setDuration] = useState(15);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  if (!lawyer?.verified || lawyer?.vacation_mode || !lawyer?.available) return null;

  const pulseUntil = lawyer?.pulse_until ? new Date(lawyer.pulse_until).getTime() : null;
  const isActive = pulseUntil && pulseUntil > now;
  const remainingSec = isActive ? Math.max(0, Math.floor((pulseUntil - now) / 1000)) : 0;
  const mm = String(Math.floor(remainingSec / 60)).padStart(2, "0");
  const ss = String(remainingSec % 60).padStart(2, "0");

  const start = async () => {
    setBusy(true);
    try {
      const r = await startPulse(duration, message.trim());
      toast.success(`Broadcasting Available Now for ${duration} minutes`);
      // Await parent reload so the active state flips without a flicker.
      await onChanged?.();
      // Belt-and-braces: also nudge `now` so the countdown computes immediately
      // even before the parent re-renders with fresh data.
      setNow(Date.now());
      // r.pulse_until is the authoritative server timestamp — exposed via the
      // updated `lawyer.pulse_until` prop after onChanged finishes. No local
      // override needed.
      void r;
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try {
      await stopPulse();
      toast.success("Pulse ended");
      await onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="lawyer-pulse-card">
      <div className="flex items-center gap-2">
        <span className={`relative inline-flex h-2 w-2 ${isActive ? "" : "opacity-30"}`}>
          {isActive && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-2 w-2 ${isActive ? "bg-emerald-500" : "bg-slate-300"}`} />
        </span>
        <p className="text-xs uppercase tracking-widest font-bold text-slate-400">Availability pulse</p>
      </div>

      {isActive ? (
        <div className="mt-3 space-y-3" data-testid="pulse-active">
          <p className="text-2xl font-extrabold text-emerald-700" style={{ fontFamily: "Poppins" }}>{mm}:{ss}</p>
          <p className="text-xs text-slate-600">
            You're broadcasting "Available Now" — clients searching {lawyer.specialization || "your specialization"} in {lawyer.city || "your city"} see your card highlighted.
          </p>
          {lawyer.pulse_message && (
            <p className="text-xs italic text-slate-500">"{lawyer.pulse_message}"</p>
          )}
          <button
            onClick={stop}
            disabled={busy}
            data-testid="pulse-stop-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />} End pulse
          </button>
        </div>
      ) : (
        <div className="mt-3 space-y-3" data-testid="pulse-inactive">
          <p className="text-xs text-slate-500">
            Tell clients you're free <strong>right now</strong>. They'll see a live emerald pulse on your card and can book instantly.
          </p>
          <div className="flex gap-1">
            {DURATIONS.map((d) => (
              <button
                key={d}
                onClick={() => setDuration(d)}
                data-testid={`pulse-duration-${d}`}
                className={`flex-1 px-2 py-1.5 text-[11px] font-bold rounded-lg border transition ${
                  duration === d ? "bg-[#0A2540] text-[#D4AF37] border-[#0A2540]" : "border-slate-200 text-slate-600 hover:border-[#0A2540]"
                }`}>
                {d} min
              </button>
            ))}
          </div>
          <input
            type="text" maxLength={140}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Optional message (e.g. 'Free for 30 min — quick consult')"
            data-testid="pulse-message-input"
            className="w-full text-xs px-3 py-2 rounded-lg border border-slate-200 focus:border-[#0A2540] outline-none"
          />
          <button
            onClick={start}
            disabled={busy}
            data-testid="pulse-start-btn"
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg bg-[#D4AF37] text-[#0A2540] border border-[#B8941F] hover:opacity-90">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />} Broadcast Available Now
          </button>
        </div>
      )}
    </div>
  );
}
